import { ClassFlag } from "./ClassFlag";
import { ElementAttribute } from "./ElementAttribute";
import { ISelectable } from "./ISelectable";
import { HTMLEditor } from "./HTMLEditor";
import { ElementSelector } from "./ElementSelector";
import { AttributeValue } from "./AttributeValue";
import { MultiString } from "../i18n/MultiString";
import { LanguageCodes } from "../i18n/LanguageCode";
import { Selector } from "../html-editor/selection/SelectorUI";
import { OnClick } from "./EventListeners";

export class ElementType<E extends Element>
{
  static readonly any = new ElementType<Element>( "*" );
  static readonly input  = new ElementType<HTMLInputElement>( "input" );

  static readonly meta = new ElementType<HTMLMetaElement>( "meta" );
  static readonly title = new ElementType<HTMLMetaElement>( "title" );

  static readonly anchor = new ElementType<HTMLAnchorElement>( "a" );
  static readonly break  = new ElementType<HTMLBRElement>( "br" );

  static readonly image  = new ElementType<HTMLImageElement>( "img" );
  static readonly audio  = new ElementType<HTMLAudioElement>( "audio" );
  static readonly video  = new ElementType<HTMLVideoElement>( "video" );
  static readonly source = new ElementType<HTMLSourceElement>( "source" );
  static readonly canvas = new ElementType<HTMLCanvasElement>( "canvas" );
  
  
  static readonly script = new ElementType<HTMLScriptElement>( "script" );
  static readonly style  = new ElementType<HTMLStyleElement>( "style" );
  static readonly form   = new ElementType<HTMLFormElement>( "form" );
  static readonly button = new ElementType<HTMLButtonElement>( "button" );
  static readonly select = new ElementType<HTMLSelectElement>( "select" );
  static readonly option = new ElementType<HTMLOptionElement>( "option" );
  static readonly textArea = new ElementType<HTMLTextAreaElement>( "textarea" );

  static readonly table = new ElementType<HTMLTableElement>( "table" );
  static readonly thead = new ElementType<HTMLTableElement>( "thead" );
  static readonly tbody = new ElementType<HTMLTableElement>( "tbody" );
  
  static readonly tr = new ElementType<HTMLTableRowElement>( "tr" );
  static readonly th = new ElementType<HTMLTableCellElement>( "th" );
  static readonly td = new ElementType<HTMLTableCellElement>( "td" );
  
  static readonly hr = new ElementType<HTMLElement>( "hr" );

  static readonly span = new ElementType<HTMLSpanElement>( "span" );
  static readonly div = new ElementType<HTMLDivElement>( "div" );
  static readonly code = new ElementType<HTMLElement>( "code" );
  static readonly pre = new ElementType<HTMLPreElement>( "pre" );

  static readonly p = new ElementType<HTMLElement>( "p" );
  static readonly b = new ElementType<HTMLElement>( "b" );
  static readonly i = new ElementType<HTMLElement>( "i" );

  static readonly h1 = new ElementType<HTMLElement>( "h1" );
  static readonly h2 = new ElementType<HTMLElement>( "h2" );
  static readonly h3 = new ElementType<HTMLElement>( "h3" );
  static readonly h4 = new ElementType<HTMLElement>( "h4" );
  static readonly h5 = new ElementType<HTMLElement>( "h5" );
  static readonly h6 = new ElementType<HTMLElement>( "h6" );
  static readonly h7 = new ElementType<HTMLElement>( "h7" );
  static readonly h8 = new ElementType<HTMLElement>( "h8" );
  static readonly h9 = new ElementType<HTMLElement>( "h9" );
  static readonly h10 = new ElementType<HTMLElement>( "h10" );

  static readonly section = new ElementType<HTMLElement>( "section" );

  static readonly iframe = new ElementType<HTMLIFrameElement>( "iframe" );

  static readonly svg = new ElementType<SVGElement>( "svg" ); 

  static readonly strong = new ElementType( "strong" ); 

  static readonly portraitOnly = new ElementType( "portrait--only" );
  static readonly landscapeOnly = new ElementType( "landscape--only" );

  static createLink( children:(Node | string) | (Node | string)[], href:string, target:string )
  {
    let anchor = ElementType.anchor.create( children );
    ElementAttribute.href.to( anchor, href );

    if ( target != null )
    {
      ElementAttribute.target.to( anchor, target );
    }

    return anchor;

  }

  static queryDoc( type:string )
  {
    return new ElementType( type ).queryDoc();
  }
  
  static create( type:string )
  {
    return document.createElement( type );
  }

  static createFrom( element:Element, source:ElementAttribute )
  {
    return ElementType.create( source.from( element ) );
  }

  private _type:string;
  private _rawType:string;
  private _namespace:string;

  public constructor( type:string, namespace:string = undefined )
  {
    this._rawType = type;
    this._type = this._rawType.toUpperCase();

    if ( namespace !== undefined )
    {
      this._namespace = namespace;
    }
  }


  get type()
  {
    return this._type;
  }

  get rawType()
  {
    return this._rawType;
  }

  get selector()
  {
    return this._type;
  }

  selectorWith( selectable:ISelectable[] )
  {
    let selectorString = this.selector;

   
    for ( let i = 0; selectable && i < selectable.length; i++ )
    {
      selectorString += selectable[ i ].selector;
    }

    return selectorString;
  }

  isParent( child:Element ):boolean
  {
    let p = child.parentElement;

    while ( p != null )
    {
      if ( this.is( p ) )
      {
        return true;
      }

      p = p.parentElement;
    }

    return false;
  }

  getParent( child:Element ):E
  {
    if ( child == null )
    {
      return null;
    }

    let it = child.parentElement;

    while ( it )
    {
      if ( this.is( it ) )
      {
        return it as any as E;
      }

      it = it.parentElement;
    }

    return null;
  }

  static getClosestParent( child:Element, types:ElementType<Element>[] )
  {
    let it = child.parentElement;

    while ( it )
    {
      let matchingParent = types.find( t => t.is( it ) ); 

      if ( matchingParent )
      {
        return it;
      }

      it = it.parentElement;
    }

    return null;
  }

  forAll( target:Element|Document, callback:(element:E)=>any)
  {
    let elements = this.queryAll( target );
    
    for ( let i = 0; i < elements.length; i++ )
    {
      callback( elements[ i ] );
    }
  }

  forAllInDoc( callback:(element:E)=>any)
  {
    this.forAll( document, callback );
  }

  queryDoc():E
  {
    return document.querySelector( this._rawType ) as E;
  }

  query( element:Document|Element ):E
  {
    if ( ! element )
    {
      return null;
    }
    
    return element.querySelector( this._rawType ) as E;
  }

  queryDirectChild( element:Document|Element, fromStart:boolean = true )
  {
    let start = 0;
    let update = 1;
    let end = element.childNodes.length;
    
    if ( ! fromStart )
    {
      start = element.childNodes.length - 1;
      update = -1;
      end = -1;
    }


    for ( let i = start; i != end; i += update )
    {
      let child = element.childNodes[ i ];

      if ( child.nodeType !== Node.ELEMENT_NODE )
      {
        continue;
      }

      let childElement = child as Element;
      
      if ( childElement.matches( this.selector ) )
      {
        return childElement;
      }
    }
  }

  queryDirectChildren( element:Document|Element )
  {    
    let children:Element[] = [];

    for ( let i = 0; i < element.childNodes.length; i ++ )
    {
      let child = element.childNodes[ i ];

      if ( child.nodeType !== Node.ELEMENT_NODE )
      {
        continue;
      }

      let childElement = child as Element;
      
      if ( ! childElement.matches( this.selector ) )
      {
        continue;
      }

      children.push( childElement );
    }

    return children;
  }
 

  queryTextContent( element:Document|Element, alternative:string = "" ):string
  {
    let q = this.query( element );

    if ( ! q )
    {
      return alternative;
    }

    return q.textContent;
  }

  queryWith( element:Document|Element, ...atts:(ElementAttribute|AttributeValue|ClassFlag)[] ):Element
  {
    let selector = new ElementSelector( this );
    selector.attributes.apply( selector, atts );

    return selector.query( element );
  }

  queryWithAll( element:Document|Element, ...atts:(ElementAttribute|AttributeValue|ClassFlag)[] ):Element[]
  {
    let selector = new ElementSelector( this );
    selector.attributes.apply( selector, atts );

    return selector.queryAll( element );
  }

  queryAll( element:Document|Element ):E[]
  {
    if ( element == null )
    {
      return [];
    }

    return HTMLEditor.nodeListToArray( element.querySelectorAll( this._rawType ) ) as E[];
  }

  forAllOnClick( e:Document|Element, callback:( el:HTMLElement, ev:MouseEvent )=>void)
  {
    let elements = this.queryAll( e );
    elements.forEach(
      cel => 
      {
        let htmlElement = cel as any as HTMLElement;
        htmlElement.addEventListener( "click", 
          ( cev )=>
          {
            callback( htmlElement, cev );
          }
        );

        
      }
    );
  }

  create( children:(Node|string)|(Node|string)[] = undefined, doc:Document = undefined ):E
  {
    doc = doc || document;

    let anyElement =
    this._namespace === undefined ?  doc.createElement( this._rawType ) 
                                  :  doc.createElementNS( this._namespace, this._rawType ); 
    
    let element = anyElement as any as E;

    if ( children )
    {
      if ( Array.isArray( children ) )
      {
        for ( let i = 0; i < children.length; i++ )
        {
          if ( typeof children[ i ] === "string" )
          {
            element.appendChild( doc.createTextNode( children[ i ] as string ) );
          }
          else
          {
            element.appendChild( children[ i ] as Node );
          }
        } 
      }
      else
      {
        if ( typeof children === "string" )
        {
          element.appendChild( doc.createTextNode( children as string ) );
        }
        else
        {
          element.appendChild( children as Node );
        }
      }
      
           
    }

    return element;
  }

  setText( element: Element, text:string )
  {
    let doc = element.ownerDocument
    
    if ( ! this.is( element ) )
    {
      element = this.query( element );
    }

    element.innerHTML = "";
    element.appendChild( doc.createTextNode( text ) );
  }


  public is( element:Element )
  {
    if ( ! element )
    {
      return false;
    }

    if ( ! element.nodeName )
    {
      return false;
    }

    if ( this._rawType === "*" )
    {
      return true;
    }

    return this._type.toLowerCase() === element.nodeName.toLowerCase();
  }

  mapChildren<T>( element:Element, childType:ElementType<Element>, mapper:( element:Element )=> T, alternative:T[] = null ):T[]
  {
    let p = this.query( element );

    if ( ! p )
    {
      return null;
    }

    let children = childType.queryAll( p );

    return children.map( c => mapper( c ) );
  }

  mapChildrenAsEnums<T>(element:Element, childType:ElementType<Element>, enumType:any, alternative:T[] = null ):T[]
  {
    let childMapper = ( c:Element ) => ElementType.getEnumValue<T>( c.textContent, enumType );
    return this.mapChildren( element, childType, childMapper );
  }

  mapChildrenAsString(element:Element, childType:ElementType<Element>, alternative:string[] = [] ):string[]
  {
    let childMapper = ( c:Element ) => c.textContent.trim();
    return this.mapChildren( element, childType, childMapper );
  }

  textContent( element:Element|Document, alternative:string = null )
  {
    let e = this.query( element );

    if ( ! e )
    {
      return alternative;
    }

    return e.textContent;
  }

  static getMultiStringValue( msElement:Element )
  {
    let ms = new MultiString();

    let languages = LanguageCodes.all;

    languages.forEach(
      ( lc )=>
      {
        let elementType = LanguageCodes.htmlType( lc );
        let langElement = elementType.query( msElement );

        if ( ! langElement )
        {
          return;
        }

        let value = langElement.textContent.trim();

        MultiString.set( ms, lc, value );
      }
    );


    return ms;
  }

  multiString( element: Element, alternative:MultiString = null )
  {
    let e = this.query( element );

    if ( ! e )
    {
      return alternative;
    }

    let msElement = MultiString.htmlType.query( e );

    if ( ! msElement )
    {
      return alternative;
    }

    
    return ElementType.getMultiStringValue( msElement );

  }

  asEnumOrMultiString<E>( element:Element, enumType:any ):E|MultiString
  {
    let ms = MultiString.htmlType.query( element );

    if ( ! ms )
    {
      return this.asEnum<E>( element, enumType );
    }

    return this.multiString( element );
  }

  static getEnumOrMultiString<E>( element:Element, enumType:any):E|MultiString
  {
    let ms = MultiString.htmlType.query( element );

    if ( ! ms )
    {
      return this.getEnumValue<E>( element.textContent, enumType );
    }

    return this.getMultiStringValue( element );
  }

  static getEnumValue<E>( value:string, enumType:any )
  {
    if ( ! value )
    {
      console.log( "asEnum: No value", value );
      return null;
    }

    value = value.trim();

    if ( /d+/.test( value ) )
    {
      let intValue = parseInt( value );

      if ( enumType[ intValue ] )
      {
        return intValue as any as E;
      }

      return null;
    }

    

    return enumType[ value ] as E;
  }

  asEnum<E>( element:Element, enumType:any )
  {
    let value = this.textContent( element );

    if ( ! value )
    {
      console.log( "asEnum: No value", element.tagName, element.innerHTML );
      return null;
    }

    return ElementType.getEnumValue<E>( value, enumType );

  } 

}